import { useCallback, useEffect, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import { cs } from '@/_shared';

export type CheckboxProps = React.ComponentProps<'input'> & { wrapperClassName?: string; indeterminate?: boolean };

export const Checkbox = ({ className, wrapperClassName, ref, indeterminate = false, ...rest }: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.RefObject<HTMLInputElement | null>).current = node;
      }
    },
    [ref]
  );

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={twMerge('relative inline-flex h-5 w-5 items-center', wrapperClassName)}>
      <input
        {...rest}
        ref={setInputRef}
        checked={rest.checked}
        aria-checked={indeterminate ? 'mixed' : rest.checked}
        type="checkbox"
        className={twMerge(
          "before:content[''] peer border-c-border checked:border-primary checked:bg-primary checked:before:bg-primary indeterminate:border-primary indeterminate:bg-primary indeterminate:before:bg-primary before:bg-primary relative h-5 w-5 cursor-pointer appearance-none border transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-3 before:w-3 before:-translate-x-2/4 before:-translate-y-2/4 before:rounded-full before:opacity-0 before:transition-opacity hover:before:opacity-10 dark:border-gray-400/70",
          'disabled:cursor-default! disabled:border',
          'rounded-md',
          cs('rounded-2xl'),
          className
        )}
      />
      <span className="pointer-events-none absolute top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 text-white opacity-0 transition-opacity peer-checked:opacity-100 peer-indeterminate:opacity-100">
        {indeterminate ? (
          <div className="h-0.5 w-2.5 rounded-sm bg-current" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5"
            viewBox="0 0 20 20"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
    </div>
  );
};
